import { EChartOption } from 'echarts';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import { ReportDto } from '@/core';
import { getDefaultOptions } from './getDefaultOptions';
import { displayCurrency } from '@/utils/displayCurrency';

export enum TotalChartTotalsTypesEnum {
  costs = 'Sum $',
  hours = 'Hours',
}

const tooltipFormatter = (param: EChartOption.Tooltip.Format[], totalType: TotalChartTotalsTypesEnum) => {
  const formattedValue = (data: number) =>
    totalType === TotalChartTotalsTypesEnum.hours
      ? `${displayCurrency(data)}hrs`
      : `$${displayCurrency(data)}`;
  const formattedSeriesName = (seriesName?: string) =>
    totalType === TotalChartTotalsTypesEnum.costs ? 'Total budget' : `${seriesName}`;
  const valuesData = param
    .map(
      ({ marker, seriesName, data }) => `
        <div class="echarts-tooltip-content-cost">
          <div>${marker}
            <span>${formattedSeriesName(seriesName)}</span>
          </div>
          <div class="echarts-tooltip-value">${formattedValue(data.at(1))}</div>
        </div>`,
    )
    .join('');

  return `<div>
          ${moment(param[0].axisValue).format('MMMM, YYYY')}
        </div>
        <div class="echarts-tooltip-content">
         ${valuesData}
        </div>`;
};

const getSeries = (name: string, data: EChartOption.Series[]) => [
  {
    name,
    id: 'series',
    data,
    type: 'bar',
    barWidth: 14,
    itemStyle: {
      borderRadius: [7, 7, 0, 0],
    },
    emphasis: {
      itemStyle: {
        borderWidth: 4,
        borderColor: '#1677FF66',
      },
    },
  },
];

const getLegend = (name: string) => ({
  selectedMode: false,
  bottom: 16,
  padding: [0, 16],
  itemWidth: 8,
  itemHeight: 8,
  data: [
    {
      name,
      icon: 'circle',
      textStyle: {
        fontSize: 12,
      },
    },
  ],
});

const getTotalCostsOptions = (
  isChartExpanded: boolean,
  sortedSourceReportsByDate: ReportDto[],
  maxShownColumns: number,
) => {
  const defaultOptions = getDefaultOptions(isChartExpanded, maxShownColumns);
  const seriesDataCosts = sortedSourceReportsByDate.map(({ metricsDate, totalCost }) => [
    metricsDate,
    totalCost,
  ]);

  return {
    ...defaultOptions,
    tooltip: {
      className: 'echarts-tooltip',
      trigger: 'axis',
      formatter: (param: EChartOption.Tooltip.Format[]) =>
        tooltipFormatter(param, TotalChartTotalsTypesEnum.costs),
    },
    yAxis: {
      axisLabel: {
        formatter: (value: number) => (value ? `$${value / 1000}K` : value),
      },
    },
    series: getSeries('Total Sum $', seriesDataCosts),
    legend: getLegend('Total Sum $'),
  } as EChartOption;
};

const getTotalHoursOptions = (
  isChartExpanded: boolean,
  sortedSourceReportsByDate: ReportDto[],
  maxShownColumns: number,
) => {
  const defaultOptions = getDefaultOptions(isChartExpanded, maxShownColumns);
  const seriesDataHours = sortedSourceReportsByDate.map(({ metricsDate, totalHours }) => [
    metricsDate,
    totalHours,
  ]);

  return {
    ...defaultOptions,
    yAxis: {
      axisLabel: {
        formatter: (value: number) => (value ? `${value / 1000}K hrs` : value),
      },
    },
    series: getSeries('Total Hours', seriesDataHours),
    legend: getLegend('Total Hours'),
    tooltip: {
      className: 'echarts-tooltip',
      trigger: 'axis',
      formatter: (param: EChartOption.Tooltip.Format[]) =>
        tooltipFormatter(param, TotalChartTotalsTypesEnum.hours),
    },
  } as EChartOption;
};

export const getOptionsTotalChart = (reports: ReportDto[], totalType: string, maxShownColumns: number) => {
  const sourceReports = [...reports].filter((report) => report.isDataSource);
  const sortedSourceReportsByDate = orderBy(sourceReports, [(obj) => new Date(obj.metricsDate)], ['asc']);

  const isChartExpanded = sourceReports.length > maxShownColumns;

  const totalCostsOptions = getTotalCostsOptions(isChartExpanded, sortedSourceReportsByDate, maxShownColumns);
  const totalHoursOptions = getTotalHoursOptions(isChartExpanded, sortedSourceReportsByDate, maxShownColumns);

  const options = totalType === TotalChartTotalsTypesEnum.costs ? totalCostsOptions : totalHoursOptions;

  return {
    options,
    isChartExpanded,
  };
};
